import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { useParams } from 'react-router-dom'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import useToast from 'hooks/useToast'
import {
  useUploadClanAvatarMutation,
  useGetClanPowerQuery,
  useGetClanQuery,
  useGetMyMembershipQuery,
  useInviteToClanMutation,
  useLeaveClanMutation,
  useRemoveClanMemberMutation,
  useTransferClanLeadershipMutation
} from 'state/services/community/community'
import { avatarUrl } from 'utils/avatarUrl'
import { Card, Grow, Identity, Muted, Row, shortWallet } from './components/shared'
import { useSession } from './components/useSession'

const StyledTitle = styled(StyledBiennale)`
  font-size: 28px;
`

/* See the Community index: MainWrapper is a row Flex with no vertical offset. */
const Column = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 152px 0 120px;
`

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  outline: none;
  width: 100%;
`

const Actions = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
`

const ClanAvatar = styled.div<{ $src?: string }>`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme, $src }) => ($src ? `url(${$src}) center/cover no-repeat` : theme.colors.gray9)};
`

const HiddenFileInput = styled.input`
  display: none;
`

const AvatarButton = styled.label`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  font-size: 13px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
`

function errorText(error: unknown, fallback: string): string {
  const message = (error as { data?: { message?: string } })?.data?.message
  return typeof message === 'string' && message ? message : fallback
}

const ClanView: React.FC = () => {
  const { tag = '' } = useParams()
  const { toastError, toastSuccess } = useToast()
  const { account, accessToken, ready } = useSession()
  const session = { account: account as string, accessToken: accessToken as string }

  const clan = useGetClanQuery({ tag }, { skip: !tag })
  const membership = useGetMyMembershipQuery(session, { skip: !ready })
  const power = useGetClanPowerQuery({ clanId: clan.data?.clan?.id as string }, { skip: !clan.data?.clan?.id })

  const [invite, { isLoading: inviting }] = useInviteToClanMutation()
  const [leave] = useLeaveClanMutation()
  const [removeMember] = useRemoveClanMemberMutation()
  const [transfer] = useTransferClanLeadershipMutation()

  const [inviteWallet, setInviteWallet] = useState('')
  const [uploadClanAvatar, { isLoading: uploadingAvatar }] = useUploadClanAvatarMutation()

  const onPickAvatar = async (file: File | undefined) => {
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toastError('That picture is too large', 'Pick one under 2 MB.')
      return
    }
    await run(
      () => uploadClanAvatar({ ...session, file }).unwrap(),
      'Clan picture saved',
      'Could not save that picture'
    )
  }

  const iLeadThis = ready && membership.data?.role === 'leader' && membership.data?.clan?.id === clan.data?.clan?.id
  const iAmIn = ready && membership.data?.clan?.id === clan.data?.clan?.id

  /*
   * Every destructive action on this page goes through `run` — transferring
   * leadership, removing a member, leaving or dissolving the clan — so the
   * in-flight guard belongs here rather than three times over. Without it a
   * second press fires the same irreversible thing again and is answered with
   * an error about a state the first press had already reached.
   *
   * It lives ABOVE the early returns because React counts hooks, not
   * intentions: declared after them, it ran on the render that had data and
   * not on the render that was still loading, and the second render threw
   * "Rendered more hooks than during the previous render" (#310) — a white
   * page on every cold open of a clan, twelve times over two days, mostly on
   * phones where nothing was cached to skip the loading render.
   */
  const [busy, setBusy] = useState(false)

  if (clan.isLoading) {
    return (
      <MainWrapper>
        <Column>
          <Muted variant={TextVariant.BODY_SMALL}>Loading…</Muted>
        </Column>
      </MainWrapper>
    )
  }
  if (!clan.data?.clan) {
    return (
      <MainWrapper>
        <Column>
          <StyledTitle>Clan not found</StyledTitle>
          <Muted variant={TextVariant.BODY_SMALL}>Nothing answers to @{tag}. It may have been dissolved.</Muted>
        </Column>
      </MainWrapper>
    )
  }

  const run = async (fn: () => Promise<unknown>, ok: string, bad: string) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      toastSuccess(ok)
    } catch (error) {
      toastError(bad, errorText(error, 'Try again shortly.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <MainWrapper>
      <Column>
        <StyledTitle>{clan.data.clan.name}</StyledTitle>
        <Muted variant={TextVariant.BODY_SMALL}>
          @{clan.data.clan.tag} · {clan.data.clan.memberCount} {clan.data.clan.memberCount === 1 ? 'member' : 'members'}{' '}
          · {clan.data.clan.openToRequests ? 'open to requests' : 'invitation only'}
        </Muted>
        {clan.data.clan.description ? <Text variant={TextVariant.BODY_SMALL}>{clan.data.clan.description}</Text> : null}

        <Card style={{ marginTop: 20 }}>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
            Pooled voting power
            <InfoIcon
              iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
              tooltipProps={{
                title:
                  "The sum of its members' voting power. On a DAO proposal the members vote inside the clan first; the power of those who voted then counts double, provided at least half of them took part."
              }}
            />
          </Text>
          <Text variant={TextVariant.H4}>{power.data ? power.data.pooledVotingPower.toLocaleString() : '—'}</Text>
        </Card>

        <Card style={{ marginTop: 16 }}>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>Members</Text>
          {clan.data.members.map((m) => (
            <Row key={m.wallet}>
              <Identity
                wallet={m.wallet}
                name={m.profile?.name}
                tag={m.profile?.tag}
                avatarPath={m.profile?.avatarPath}
              />
              <Muted variant={TextVariant.BODY_SMALL}>{m.role === 'leader' ? 'Leader' : 'Member'}</Muted>
              {iLeadThis && m.role !== 'leader' && (
                <Actions>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => transfer({ ...session, wallet: m.wallet }).unwrap(),
                        'Leadership transferred',
                        'Could not transfer leadership'
                      )
                    }
                  >
                    Make leader
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => removeMember({ ...session, wallet: m.wallet }).unwrap(),
                        'Removed',
                        'Could not remove them'
                      )
                    }
                  >
                    Remove
                  </Button>
                </Actions>
              )}
            </Row>
          ))}
        </Card>

        {iLeadThis && (
          <Card style={{ marginTop: 16 }}>
            <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>Clan picture</Text>
            <Flex style={{ alignItems: 'center', gap: 16 }}>
              <ClanAvatar $src={avatarUrl(clan.data.clan.avatarPath)} />
              <div>
                <HiddenFileInput
                  id="clan-avatar"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => void onPickAvatar(e.target.files?.[0])}
                />
                <AvatarButton htmlFor="clan-avatar">{uploadingAvatar ? 'Uploading…' : 'Choose a picture'}</AvatarButton>
                <Muted variant={TextVariant.BODY_SMALL}> PNG, JPEG, WebP or GIF, up to 2 MB.</Muted>
              </div>
            </Flex>
          </Card>
        )}

        {iLeadThis && (
          <Card style={{ marginTop: 16 }}>
            <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>Invite somebody</Text>
            <Input placeholder="0x…" value={inviteWallet} onChange={(e) => setInviteWallet(e.target.value.trim())} />
            <Button
              size="sm"
              disabled={inviting || !/^0x[0-9a-fA-F]{40}$/.test(inviteWallet)}
              onClick={() =>
                run(
                  async () => {
                    await invite({ ...session, wallet: inviteWallet }).unwrap()
                    setInviteWallet('')
                  },
                  'Invitation sent',
                  'Could not send that invitation'
                )
              }
            >
              {inviting ? 'Sending…' : 'Send invitation'}
            </Button>
          </Card>
        )}

        {iAmIn && (
          <Card style={{ marginTop: 16 }}>
            {/*
            A leader is told the rule before they press it, not after the
            server refuses. The refusal is right — auto-promoting a stranger or
            dissolving other people's membership are both worse — but a rule
            you only meet as an error reads as a bug.
          */}
            {iLeadThis && clan.data.clan.memberCount > 1 ? (
              <Muted variant={TextVariant.BODY_SMALL}>
                Transfer leadership before leaving
                <InfoIcon
                  iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
                  tooltipProps={{
                    title:
                      'A leader cannot walk out of a clan that still has members: promoting somebody automatically would hand a stranger control of a group they did not ask to run, and dissolving it would end other people\u2019s membership on one person\u2019s decision.'
                  }}
                />
              </Muted>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  run(
                    () => leave(session).unwrap(),
                    iLeadThis ? 'Clan dissolved' : 'You left the clan',
                    'Could not leave'
                  )
                }
              >
                {iLeadThis ? 'Dissolve clan' : 'Leave clan'}
              </Button>
            )}
          </Card>
        )}
      </Column>
    </MainWrapper>
  )
}

export default ClanView
